// 全体像 PDF・印刷（canvas-pdf-export.ts の Canvas2D 描画）に写る図形の外接矩形（ステージ座標）を求める純関数群。
//
// 「PDF 出力範囲の外へ図形を置いたか」の判定（PDF 出力範囲の通知）に使う。保存値の x / y / w / h だけでは、
// 回転・線の太さ・ペンの点群・コネクタの経路と矢じりによる描画のはみ出しを判定できないため、
// 出力側の描画規則（図形中心まわりの回転、線は中心線から太さの半分ずつ広がる等）をここで再現する。
//
// 出力解像度にだけ依存する描画（付箋の 1 画素の枠線や、線幅の下限 1 画素など）は含めない。
// これらは出力幅によってステージ座標での太さが変わり、境界ぴったりに置いた図形を出力ごとに
// 範囲外と判定してしまうためである。判定はステージ座標で決まる幾何だけで行う。
//
// 図形（shape）の角の miter による突き出しなど、図形種別ごとの細かな線の形状までは再現せず、
// 線の太さの半分を、外枠（矢印では軸と矢じりの経路）からの最大のはみ出しとして近似する。

import {
  computeConnectorRoute,
  isDrawableObject,
  type CanvasFrame,
  type CanvasObject,
  type ShapeCanvasObject,
} from "@refinement-canvas/domain";
import { arrowHeadPoints } from "./connector-geometry";
import { CONNECTOR_DEFAULT_STROKE_WIDTH } from "./connector-style";
import { resolveArrowEdgePath, resolveArrowPath } from "./shape-arrow";
import { SHAPE_DEFAULT_STROKE_WIDTH } from "./shape-preference";
import { resolveRectCornerRadius } from "./shape-rect";

/** ステージ座標の外接矩形（左上・右下の辺の位置）。 */
export interface StageBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * 図形の線は外枠から内側へこの距離だけ入った位置を通る。canvas-pdf-export が drawShape へ渡す inset
 * （出力画素で 2）を、等倍出力（出力幅 = キャンバス幅）のステージ座標で表した値。縮小出力では inset が
 * 出力画素のまま変わらないためステージ座標ではより内側を通り、外へのはみ出しは等倍出力のときが最大になる。
 * 判定は、そのいちばん外側まで描かれる等倍出力に合わせる。
 */
const SHAPE_STROKE_INSET_PX = 2;
/** セクションの枠線の太さ（canvas-pdf-export は 2px を出力倍率で縮めて外枠の上へ描く）。 */
const SECTION_STROKE_WIDTH_PX = 2;
/** コネクタの矢じりの長さ（canvas-pdf-export が arrowHeadPoints へ渡す 12px と同じ値）。 */
const CONNECTOR_ARROW_HEAD_PX = 12;
/**
 * 矢印図形の矢じりの長さと片側の張り出し（canvas-pdf-export の drawShape が resolveArrowPath /
 * resolveArrowEdgePath へ渡す 12px・6px を、等倍出力のステージ座標で表した値）。
 */
const SHAPE_ARROW_HEAD_LENGTH_PX = 12;
const SHAPE_ARROW_HEAD_HALF_WIDTH_PX = 6;
/**
 * 包含判定の許容誤差（ステージ px）。回転角が 90 / 180 度のときに三角関数の丸め誤差（1e-12 程度）で
 * 境界ぴったりの図形が範囲外と判定されないようにする。1px のはみ出しは確実に範囲外と判定できる大きさに留める。
 */
const CONTAINMENT_EPSILON_PX = 1e-6;

/** id からオブジェクトを引くための索引（doc store の objects と同じ形）。 */
export type CanvasObjectLookup = Readonly<Record<string, CanvasObject | undefined>>;

/**
 * 全体像 PDF に描画される図形の外接矩形を返す。PDF に描画されない図形（コメントスレッド、
 * 端点が見つからず描画されないコネクタ、座標が有限値でない図形）は null を返す。
 */
export function computeCanvasObjectVisualBounds(
  object: CanvasObject,
  objects: CanvasObjectLookup
): StageBounds | null {
  if (!isDrawableObject(object.type)) return null;
  if (!isFiniteFrame(object)) return null;
  const rotation = finiteOr(object.rotation, 0);
  switch (object.type) {
    case "pen-stroke": {
      if (object.points.length === 0) return null;
      // 点群はオブジェクト中心まわりに回転して描かれ、丸い線端・角で太さの半分だけ外へ広がる。
      const center = frameCenter(object);
      const points = object.points.map((point) => rotateAround(point, center, rotation));
      return expandBounds(boundsOfPoints(points), Math.max(0, finiteOr(object.thickness, 0)) / 2);
    }
    case "connector": {
      const from = objects[object.from.objectId];
      const to = objects[object.to.objectId];
      // 端点が見つからないコネクタは PDF に描かれない（canvas-pdf-export と同じ扱い）。
      if (!from || !to || !isFiniteFrame(from) || !isFiniteFrame(to)) return null;
      const route = computeConnectorRoute(
        { x: from.x, y: from.y, w: from.w, h: from.h, rotation: finiteOr(from.rotation, 0) },
        object.from.side,
        { x: to.x, y: to.y, w: to.w, h: to.h, rotation: finiteOr(to.rotation, 0) },
        object.to.side
      );
      if (route.length < 2) return null;
      const points = [...route];
      const tip = route[route.length - 1];
      const before = route[route.length - 2];
      if (tip && before) {
        const head = arrowHeadPoints(tip, before, CONNECTOR_ARROW_HEAD_PX);
        if (head) points.push(head.left, head.right);
      }
      // 出力はコネクタ自身の保存枠の中心まわりにも回転させてから経路を描く。
      const center = frameCenter(object);
      const rotated = points.map((point) => rotateAround(point, center, rotation));
      const strokeWidth = Math.max(0, finiteOr(object.strokeWidth, CONNECTOR_DEFAULT_STROKE_WIDTH));
      return expandBounds(boundsOfPoints(rotated), strokeWidth / 2);
    }
    case "section":
      // 枠線は外枠の上に太さの中心が来るため、太さの半分だけ外へはみ出す。
      return rotatedFrameBounds(object, rotation, SECTION_STROKE_WIDTH_PX / 2);
    case "shape": {
      // 塗りも線も透明な図形は、PDF に画素を 1 つも描かない（描かれない図形として扱う）。
      if (isInvisibleShape(object)) return null;
      // 線が透明（線なし）なら、線幅の設定に関わらず線は画素を描かないため、塗りの経路だけで判定する。
      const strokeVisible = object.stroke === undefined || !isTransparentColor(object.stroke);
      const strokeWidth = strokeVisible
        ? Math.max(0, finiteOr(object.strokeWidth, SHAPE_DEFAULT_STROKE_WIDTH))
        : 0;
      const center = frameCenter(object);
      if (object.shapeKind === "arrow") {
        // 矢印は外枠の辺・角の上まで軸と矢じりを引く（細長い対角矢印では矢じりの翼が枠を越える）ため、
        // 実際の経路の点と線の太さの半分で求める。
        const points = arrowStagePoints(object).map((point) =>
          rotateAround(point, center, rotation)
        );
        return expandBounds(boundsOfPoints(points), strokeWidth / 2);
      }
      // 矩形・円は外枠から SHAPE_STROKE_INSET_PX 内側の経路（円は楕円、矩形は角丸矩形）を塗って線を引くため、
      // その経路を回転した外接矩形を、線の太さの半分だけ広げる。
      if (object.shapeKind === "circle") {
        return rotatedEllipseBounds(
          center,
          Math.max(1, object.w / 2 - SHAPE_STROKE_INSET_PX),
          Math.max(1, object.h / 2 - SHAPE_STROKE_INSET_PX),
          rotation,
          strokeWidth / 2
        );
      }
      const innerW = Math.max(0, object.w - SHAPE_STROKE_INSET_PX * 2);
      const innerH = Math.max(0, object.h - SHAPE_STROKE_INSET_PX * 2);
      return rotatedRoundedRectBounds(
        center,
        innerW / 2,
        innerH / 2,
        resolveRectCornerRadius(innerW, innerH),
        rotation,
        strokeWidth / 2
      );
    }
    default:
      // 付箋・表・画像・動画・スタンプは保存枠の内側へ描かれる（はみ出しは枠でクリップ）。
      // テキストは画面の本文の実寸に枠の高さが追随するため、等倍の出力では本文がほぼ枠に収まる（縮小出力で
      // 文字サイズの下限により本文が枠を越える分は、出力解像度に依存する描画として含めない）。
      // リンクカードのタイトルは PDF では枠でクリップされずに描かれるが、文字の実寸は出力環境のフォントに
      // 依存するため、ここでは保存枠で判定する。
      return rotatedFrameBounds(object, rotation, 0);
  }
}

/** 外接矩形が PDF 出力範囲（キャンバス矩形）に完全に含まれるか。辺の上ぴったりは内側とみなす。 */
export function isBoundsInsideFrame(bounds: StageBounds, frame: CanvasFrame): boolean {
  return (
    bounds.left >= frame.x - CONTAINMENT_EPSILON_PX &&
    bounds.top >= frame.y - CONTAINMENT_EPSILON_PX &&
    bounds.right <= frame.x + frame.w + CONTAINMENT_EPSILON_PX &&
    bounds.bottom <= frame.y + frame.h + CONTAINMENT_EPSILON_PX
  );
}

/**
 * 図形の描画の一部でも PDF 出力範囲の外へ出ているか。PDF に描画されない図形は範囲外として扱わない
 * （通知の対象にしない）。
 */
export function isObjectOutsidePdfFrame(
  object: CanvasObject,
  objects: CanvasObjectLookup,
  frame: CanvasFrame
): boolean {
  const bounds = computeCanvasObjectVisualBounds(object, objects);
  return bounds !== null && !isBoundsInsideFrame(bounds, frame);
}

/**
 * 塗りも線も透明で、PDF に何も描かない図形か。矢印は線だけで描くため線が透明なら描かれず、矩形・円は
 * 塗り（未設定は透明）と線の両方が透明なときに描かれない。
 */
function isInvisibleShape(object: ShapeCanvasObject): boolean {
  const strokeTransparent = object.stroke !== undefined && isTransparentColor(object.stroke);
  if (object.shapeKind === "arrow") return strokeTransparent;
  const fillTransparent = object.fill === undefined || isTransparentColor(object.fill);
  return fillTransparent && strokeTransparent;
}

/** CSS の色の値が完全に透明か（`transparent`、または不透明度 0 の rgba / hsla）。 */
function isTransparentColor(color: string): boolean {
  const normalized = color.trim().toLowerCase();
  if (normalized === "transparent") return true;
  return /^(?:rgba|hsla)\([^)]*[,/]\s*0(?:\.0*)?%?\s*\)$/.test(normalized);
}

/**
 * 矢印図形の軸の両端と矢じりの翼の先端（ステージ座標、回転前）。canvas-pdf-export の drawShape と同じく、
 * 起点辺・起点角の指定があればその経路を、無い旧データは左から右への水平な矢印を描く。
 */
function arrowStagePoints(object: ShapeCanvasObject): { readonly x: number; readonly y: number }[] {
  const { w, h } = object;
  let local: readonly { readonly x: number; readonly y: number }[];
  if (object.arrowFromEdge !== undefined) {
    const path = resolveArrowEdgePath(
      w,
      h,
      object.arrowFromEdge,
      SHAPE_ARROW_HEAD_LENGTH_PX,
      SHAPE_ARROW_HEAD_HALF_WIDTH_PX
    );
    local = [path.tail, path.head, path.wingA, path.wingB];
  } else if (object.arrowFromCorner !== undefined) {
    const path = resolveArrowPath(
      w,
      h,
      object.arrowFromCorner,
      SHAPE_ARROW_HEAD_LENGTH_PX,
      SHAPE_ARROW_HEAD_HALF_WIDTH_PX
    );
    local = [path.tail, path.head, path.wingA, path.wingB];
  } else {
    local = [
      { x: 4, y: h / 2 },
      { x: w - 4, y: h / 2 },
      { x: w - 12, y: h / 2 - 6 },
      { x: w - 12, y: h / 2 + 6 },
    ];
  }
  return local.map((point) => ({ x: object.x + point.x, y: object.y + point.y }));
}

/**
 * 中心 center・半径 a（横）/ b（縦）の楕円を rotationDeg 回転したときの外接矩形を、pad だけ広げる。
 * 楕円の線の外側の輪郭は楕円にはならないが、その外接矩形は楕円の外接矩形を線の太さの半分だけ広げたものに等しい。
 */
function rotatedEllipseBounds(
  center: { readonly x: number; readonly y: number },
  a: number,
  b: number,
  rotationDeg: number,
  pad: number
): StageBounds {
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const extentX = Math.hypot(a * cos, b * sin) + pad;
  const extentY = Math.hypot(a * sin, b * cos) + pad;
  return {
    left: center.x - extentX,
    top: center.y - extentY,
    right: center.x + extentX,
    bottom: center.y + extentY,
  };
}

/**
 * 中心 center・半幅 halfW・半高 halfH・角丸半径 radius の角丸矩形を rotationDeg 回転したときの外接矩形を、
 * pad だけ広げる。角丸矩形は「角丸の半径だけ内側の矩形」を半径 radius で太らせた形のため、その内側の矩形を
 * 回転した外接矩形を radius だけ広げればよい。
 */
function rotatedRoundedRectBounds(
  center: { readonly x: number; readonly y: number },
  halfW: number,
  halfH: number,
  radius: number,
  rotationDeg: number,
  pad: number
): StageBounds {
  const innerHalfW = Math.max(0, halfW - radius);
  const innerHalfH = Math.max(0, halfH - radius);
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const extentX = innerHalfW * cos + innerHalfH * sin + radius + pad;
  const extentY = innerHalfW * sin + innerHalfH * cos + radius + pad;
  return {
    left: center.x - extentX,
    top: center.y - extentY,
    right: center.x + extentX,
    bottom: center.y + extentY,
  };
}

/** 保存枠を中心まわりに回転したときの外接矩形。pad は回転前に四辺へ等しく足す余白。 */
function rotatedFrameBounds(frame: CanvasFrame, rotationDeg: number, pad: number): StageBounds {
  const center = frameCenter(frame);
  const halfW = frame.w / 2 + pad;
  const halfH = frame.h / 2 + pad;
  if (rotationDeg % 360 === 0) {
    return {
      left: center.x - halfW,
      top: center.y - halfH,
      right: center.x + halfW,
      bottom: center.y + halfH,
    };
  }
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  const extentX = halfW * cos + halfH * sin;
  const extentY = halfW * sin + halfH * cos;
  return {
    left: center.x - extentX,
    top: center.y - extentY,
    right: center.x + extentX,
    bottom: center.y + extentY,
  };
}

/** 点を中心まわりに時計回り（y 下向きの画面座標系）へ回転する。Canvas2D の rotate と同じ向き。 */
function rotateAround(
  point: { readonly x: number; readonly y: number },
  center: { readonly x: number; readonly y: number },
  rotationDeg: number
): { readonly x: number; readonly y: number } {
  if (rotationDeg % 360 === 0) return { x: point.x, y: point.y };
  const radians = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  return { x: center.x + dx * cos - dy * sin, y: center.y + dx * sin + dy * cos };
}

function boundsOfPoints(
  points: readonly { readonly x: number; readonly y: number }[]
): StageBounds {
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  for (const point of points) {
    if (point.x < left) left = point.x;
    if (point.y < top) top = point.y;
    if (point.x > right) right = point.x;
    if (point.y > bottom) bottom = point.y;
  }
  return { left, top, right, bottom };
}

function expandBounds(bounds: StageBounds, pad: number): StageBounds {
  return {
    left: bounds.left - pad,
    top: bounds.top - pad,
    right: bounds.right + pad,
    bottom: bounds.bottom + pad,
  };
}

function frameCenter(frame: CanvasFrame): { readonly x: number; readonly y: number } {
  return { x: frame.x + frame.w / 2, y: frame.y + frame.h / 2 };
}

function isFiniteFrame(frame: CanvasFrame): boolean {
  return (
    Number.isFinite(frame.x) &&
    Number.isFinite(frame.y) &&
    Number.isFinite(frame.w) &&
    Number.isFinite(frame.h)
  );
}

function finiteOr(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
